import { ChatPageView } from "./ChatPageView";
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

import useChatSidebar from "./useChatSidebar";
import useChatNavigationData from "./useChatNavigationData";
import { clearChatSession, readChatSessionId, rememberChatSession } from "./chatSessionNavigation";
import { createChatSession, exportChatCompanies, getChatArtifact, getChatSession } from "../../api/chatApi";
import { isSuccessfulCompletionStatus, useArtifactActionHandler, refreshedArtifactPatch, refreshInterruptedOwnerJob } from "./chatArtifactActionHandler";
import {createOwnerProgressObserver} from './ownerProgressState';
import {leadJobOutcomeNotice} from './chatArtifactLeadModel';
import { artifactDisplayCompanyIds, buildArtifactDisplayModel, composerArtifactActions, latestArtifactMessage } from "./chatArtifactDisplayModel";
import { exportDetail, exportFailurePayload, exportWarningFromResponse, expiredArtifactData, isExpiredArtifactDetail, normalizeExportCountKind, normalizeExportWarnings } from "./chatExportResponse";
import { artifactMessageContent, artifactResultCount, candidateToCompany, discoveryCompleteMessage, ensureOkResponse, hasActiveJob, historyMessageToMessage, isTerminalJobStatus, isArtifactActionTurn, postDiscoveryActionNotice, progressPatchFromJob, referencedArtifactDisplay, refinementSummary, responseMessage, suggestionsFromPayload } from "./chatMessageModel";

import { resumeArtifactActionTurns, hydrateLatestArtifactTurn } from "./chatArtifactActionTurn";
import useChatJobPolling from "./useChatJobPolling";
import useStalledChatTurns from "./useStalledChatTurns";
import useChatSend from "./useChatSend";
import useChatActionConfirmation from "./useChatActionConfirmation";
import useGeneratedNextSteps from "./useGeneratedNextSteps";

import { buildExportReviewModel, downloadCsvBlob, suggestedCsvFilename } from "../../helper/csvExport";

export default function Chat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [exportingId, setExportingId] = useState(null), [exportReview, setExportReview] = useState(null), [exportDialogError, setExportDialogError] = useState(null);
  const [artifactActionInFlight, setArtifactActionInFlight] = useState({});
  const activeSession = useRef(null), resumedActions = useRef(new Set());
  const [composerPrefill, setComposerPrefill] = useState(null);
  const { isMounted, waitForJob } = useChatJobPolling();
  const navigationData = useChatNavigationData({
    sessionLimit: 50,
    sessionsEnabled: true,
  });
  const { refreshCredits, refreshSessions } = navigationData;
  const showLibrary = searchParams.get('library') === '1';

  const updateAssistantMessage = useCallback((messageId, patch) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? { ...message, ...patch } : message
      )
    );
  }, []);

  const abandonProcessing = useCallback(() => {
    setProcessing(false);
    setProcessingId(null);
  }, []);
  const isAbandoned = useStalledChatTurns({ messages, processingId, updateAssistantMessage, onAbandon: abandonProcessing });

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;

    const response = await createChatSession();
    const data = ensureOkResponse(response, 'Unable to start a chat session.');
    if (!data.session_id) {
      throw new Error('Unable to start a chat session.');
    }

    activeSession.current = data.session_id;
    setSessionId(data.session_id);
    rememberChatSession(data.session_id);
    return data.session_id;
  }, [sessionId]);

  const finishJobForMessage = useCallback(
    async (messageId, jobId, context = {}) => {
      const isCancelled = () => !isMounted() || context.isCancelled?.() === true;
      const progressObserver = createOwnerProgressObserver({
        publish: (patch) => updateAssistantMessage(messageId, patch), isCancelled,
        refresh: async (artifactId) => refreshedArtifactPatch(ensureOkResponse(await getChatArtifact(artifactId),
          'Unable to refresh owner results.'), context.message || {}),
      });
      try {
        const jobData = await waitForJob(jobId, {
          initialJob: context.initialJob,
          isCancelled,
          ...(context.initialJob?.job_type === 'artifact_lead_enrichment' ? {maxPollingChunks: 12} : {}),
          onProgress: (progressData) => {
            progressObserver.observe(progressData);
            const activeCopy = progressData.status === 'retryable_failed'
              ? responseMessage(progressData, null)
              : null;
            updateAssistantMessage(messageId, {
              ...(activeCopy ? { content: activeCopy } : {}),
              ...progressPatchFromJob(progressData),
            });
          },
        });
        await progressObserver.finish();
        if (isCancelled()) return;

        if (!isTerminalJobStatus(jobData.status)) {
          updateAssistantMessage(messageId, progressPatchFromJob(jobData));
          return;
        }

        refreshCredits();
        if (!isSuccessfulCompletionStatus(jobData.status)) {
          const partial = await refreshInterruptedOwnerJob(jobData, context.message || {});
          if (isCancelled()) return;
          updateAssistantMessage(messageId, {
            ...partial,
            answerConfirmed: false,
            content: leadJobOutcomeNotice(jobData)?.message || responseMessage(jobData, 'Discovery did not complete.'),
            ...progressPatchFromJob(jobData),
            suggestions: suggestionsFromPayload(jobData),
          });
          return;
        }

        if (!jobData.artifact_id) {
          updateAssistantMessage(messageId, {
            answerConfirmed: false,
            content: responseMessage(jobData, 'Discovery completed without saved results.'),
            ...progressPatchFromJob(jobData),
            suggestions: suggestionsFromPayload(jobData),
          });
          return;
        }

        const artifactResponse = await getChatArtifact(jobData.artifact_id);
        const artifactData = ensureOkResponse(artifactResponse, 'Unable to load discovery results.');
        if (isCancelled()) return;
        const candidates = artifactData.candidates || [];
        const companies = candidates.map(candidateToCompany);
        const artifactDisplay = buildArtifactDisplayModel({
          artifactData,
          message: {
            artifactId: jobData.artifact_id,
            content: companies.length > 0 ? discoveryCompleteMessage(companies) : null,
          },
          fallbackCompanies: companies,
        });
        const summary = context.refinement
          ? refinementSummary(context.previousCount || 0, artifactResultCount(artifactDisplay) ?? companies.length)
          : null;
        const ownerNotice = leadJobOutcomeNotice(jobData);
        const partialFailureNotice = ownerNotice?.message || postDiscoveryActionNotice(jobData);

        updateAssistantMessage(messageId, {
          answerConfirmed: true,
          content: artifactMessageContent(artifactDisplay, discoveryCompleteMessage(companies)),
          companies,
          artifactDisplay,
          // handleExportCompanies prefers message.artifactId over the display model,
          // so leaving this at the parent id exports the wrong artifact.
          artifactId: jobData.artifact_id,
          ...progressPatchFromJob(jobData),
          suggestions: suggestionsFromPayload(jobData),
          refinementSummary: summary,
          exportError: null,
          exportWarning: null,
          artifactActionNotice: partialFailureNotice,
          artifactActionNoticeSeverity: ownerNotice?.severity || (partialFailureNotice ? 'warning' : 'success'),
        });
      } catch (error) {
        await progressObserver.finish();
        if (isCancelled()) return;
        updateAssistantMessage(messageId, {
          answerConfirmed: false,
          content: error.message || 'Unable to complete this chat request.',
        });
      }
    },
    [isMounted, updateAssistantMessage, waitForJob, refreshCredits]
  );

  const applySessionData = useCallback(
    async (data, isCancelled = () => false) => {
      if (!data.session_id) {
        throw new Error('Unable to load chat session.');
      }

      const restoredMessages = (data.messages || []).map(historyMessageToMessage);
      if (isCancelled()) return null;

      activeSession.current = data.session_id;
      setSessionId(data.session_id);
      rememberChatSession(data.session_id);
      setMessages(restoredMessages);
      setComposerPrefill(null);

      const active = restoredMessages.filter(hasActiveJob);
      resumeArtifactActionTurns({ messages: restoredMessages, activeJobs: resumedActions, activeSession,
        sessionId: data.session_id, setArtifactActionInFlight, waitForJob, refreshCredits, updateAssistantMessage, isMounted });
      const activeMessages = active.filter((message) => !isArtifactActionTurn(message));
      if (activeMessages.length > 0) {
        setProcessing(true);
        setProcessingId(activeMessages[activeMessages.length - 1].id);
        await Promise.all(
          activeMessages.map((message) => finishJobForMessage(message.id, message.jobId, {
            initialJob: { job_id: message.jobId, status: message.jobStatus, job_type: message.jobType },
            message,
            isCancelled,
          }))
        );
      }
      return data.session_id;
    },
    [finishJobForMessage, isMounted, refreshCredits, updateAssistantMessage, waitForJob]
  );

  const restoreSessionById = useCallback(
    async (targetSessionId, options = {}) => {
      const isCancelled = typeof options.isCancelled === 'function'
        ? options.isCancelled
        : () => false;
      const response = await getChatSession(targetSessionId);
      return applySessionData(ensureOkResponse(response, 'Unable to load chat session.'), isCancelled);
    },
    [applySessionData]
  );

  const handleRestoreFailure = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setComposerPrefill(null);
  }, []);

  const { clearRestoreError, restoreError, restoreRetry, restoreSessionWithGuard, sidebarProps } = useChatSidebar({
    sessionId,
    processing,
    restoring,
    exporting: Boolean(exportingId),
    restoreSessionById,
    refreshSessions,
    onRestoreFailure: handleRestoreFailure,
    setRestoring,
    setProcessing,
    setProcessingId,
  });

  useEffect(() => {
    let cancelled = false;
    const storedSessionId = readChatSessionId();
    if (!storedSessionId) return undefined;

    async function restoreStoredSession() {
      await restoreSessionWithGuard(storedSessionId, {
        isCancelled: () => cancelled,
        allowBusy: true,
        clearOnFailure: true,
      });
    }

    restoreStoredSession();
    return () => {
      cancelled = true;
    };
  }, [restoreSessionWithGuard]);

  const { handleSend, handleRetrySend } = useChatSend({
    sessionId, messages, setMessages, processing, setProcessing, setProcessingId, clearRestoreError, ensureSession,
    applySessionData, finishJobForMessage, isAbandoned, isMounted, refreshSessions, updateAssistantMessage,
  });

  const handleStartNewChat = useCallback(() => {
    if (processing || restoring || exportingId) return;

    clearRestoreError();
    clearChatSession();
    activeSession.current = null;
    setSessionId(null);
    setMessages([]);
    setProcessing(false);
    setProcessingId(null);
    setExportingId(null);
    setArtifactActionInFlight({});
    setComposerPrefill(null);
  }, [clearRestoreError, exportingId, processing, restoring]);

  const handleOpenLibrary = useCallback(() => {
    if (navigationData.sessionsLoading) return;
    setSearchParams({ library: '1' });
  }, [navigationData.sessionsLoading, setSearchParams]);

  useEffect(() => {
    if (navigationData.sessionsLoading) return;
    // A failed list keeps the library open so its retry stays reachable (FE #746).
    if (navigationData.sessionsError) return;
    if (navigationData.sessions.length === 0 && searchParams.get('library') === '1') {
      setSearchParams({});
    }
  }, [
    navigationData.sessions.length,
    navigationData.sessionsError,
    navigationData.sessionsLoading,
    searchParams,
    setSearchParams,
  ]);

  const handleStartNewChatFromShell = useCallback(() => {
    handleStartNewChat();
    setSearchParams({});
  }, [handleStartNewChat, setSearchParams]);

  const handleOpenSessionFromLibrary = useCallback(async (targetSessionId) => {
    setSearchParams({});
    return sidebarProps.onSelectSession?.(targetSessionId);
  }, [setSearchParams, sidebarProps]);

  const handleExportCompanies = useCallback(
    (messageId, action = null) => {
      const message = messages.find((item) => item.id === messageId);
      const companyIds = artifactDisplayCompanyIds(message);
      const artifactId = message?.artifactId || message?.artifactDisplay?.artifactId || message?.referencedArtifactId;

      if ((!artifactId && companyIds.length === 0) || exportingId) return;

      const artifact = message?.artifactDisplay || referencedArtifactDisplay(messages, artifactId) || {};
      const exportCount = artifact.exportCount;
      setExportDialogError(null);
      setExportReview({
        action,
        artifactId,
        companyIds,
        messageId,
        model: buildExportReviewModel({
          exportType: 'chat',
          sourceLabel: artifact.title || 'Chat target list',
          scopeLabel: 'Complete saved target list',
          companyCount: Number.isFinite(exportCount) ? exportCount : artifact.totalCount,
          expectedRowCount: Number.isFinite(exportCount) ? exportCount : null,
          countKind: normalizeExportCountKind(
            action?.count_kind || action?.countKind,
            exportCount
          ),
          warnings: normalizeExportWarnings(message?.exportWarning, action?.warnings, action?.warning),
          suggestedFilename: suggestedCsvFilename('chat', artifact.title),
        }),
      });
    },
    [exportingId, messages]
  );

  const confirmExportCompanies = useCallback(
    async (filename) => {
      if (!exportReview || exportingId) return;
      const { action, artifactId, companyIds, messageId } = exportReview;
      const message = messages.find((item) => item.id === messageId);
      setExportingId(messageId);
      setExportDialogError(null);
      updateAssistantMessage(messageId, { exportError: null, exportWarning: null });

      try {
        const response = await exportChatCompanies(companyIds, { artifactId, action });
        if (response?.data === undefined || response?.data === null || response.status >= 400) {
          const failurePayload = await exportFailurePayload(response);
          const detail = exportDetail(failurePayload.data);
          if (isExpiredArtifactDetail(detail)) {
            const artifactData = expiredArtifactData(detail, artifactId);
            const artifactDisplay = buildArtifactDisplayModel({
              artifactData,
              message: {
                ...message,
                artifactId,
                content: responseMessage(failurePayload.data, null),
              },
              fallbackCompanies: [],
            });
            updateAssistantMessage(messageId, {
              content: artifactDisplay.summary || responseMessage(failurePayload.data, 'This saved target list has expired.'),
              companies: [],
              artifactDisplay,
              artifactId: artifactDisplay.artifactId || artifactId,
              exportError: null,
              exportWarning: null,
              artifactActionError: null,
              artifactActionNotice: null,
              artifactWorkspace: null,
            });
            setExportReview(null);
            return;
          }

          const errorMessage = failurePayload.parsedBlob
            ? 'Unable to export this list.'
            : responseMessage(failurePayload.data, 'Unable to export this list.');
          throw new Error(errorMessage);
        }
        downloadCsvBlob(response.data, filename);
        updateAssistantMessage(messageId, {
          exportWarning: exportWarningFromResponse(response),
        });
        setExportReview(null);
      } catch (error) {
        const messageText = error?.message || 'Unable to export this list.';
        setExportDialogError(messageText);
        updateAssistantMessage(messageId, {
          exportError: messageText,
        });
      } finally {
        setExportingId(null);
      }
    },
    [exportReview, exportingId, messages, updateAssistantMessage]
  );

  const actionConfirmation = useChatActionConfirmation(`${sessionId}:${restoring}:${showLibrary}`);
  const handleRunArtifactAction = useArtifactActionHandler({
    requestConfirmation: actionConfirmation.requestConfirmation,
    setMessages, refreshCredits, sessionId, activeSession, activeJobs: resumedActions,
    messages,
    artifactActionInFlight,
    setArtifactActionInFlight,
    updateAssistantMessage,
    waitForJob,
    handleExportCompanies,
  });

  const handleSendSuggestion = useCallback((messageId, suggestion) => {
    const endpointPath = suggestion?.endpoint?.path;
    const endpointMethod = String(suggestion?.endpoint?.method || '').toUpperCase();
    const actionKey = suggestion?.artifact_action_key || suggestion?.action_key || (
      endpointPath ? suggestion?.suggestion_key : null
    );
    if (actionKey && endpointPath && endpointMethod === 'POST') {
      const owner = suggestion?.artifact_id && [...messages].reverse().find((message) =>
        (message.artifactId || message.artifactDisplay?.artifactId) === suggestion.artifact_id);
      return handleRunArtifactAction(owner?.id || messageId, actionKey, suggestion);
    }

    const prompt = typeof suggestion?.prompt === 'string' ? suggestion.prompt.trim() : '';
    if (!prompt) return undefined;
    if (suggestion?.action_type) return handleSend(prompt);
    setComposerPrefill({ text: prompt, requestedAt: Date.now() });
    return undefined;
  }, [handleRunArtifactAction, handleSend, messages]);

  const composerArtifactMessage = latestArtifactMessage(messages);
  const composerActions = composerArtifactActions(composerArtifactMessage?.artifactDisplay).map((action) => (
    action.key === 'save_to_workspace' && composerArtifactMessage?.artifactWorkspace?.workspace_id
      ? { ...action, enabled: false, disabled_reason: 'Saved as a Saved Search.' }
      : action
  ));
  const composerActionInFlightKey = artifactActionInFlight[composerArtifactMessage?.id]?.key || null;
  const handleRunComposerAction = (actionKey) => composerArtifactMessage?.id
    ? handleRunArtifactAction(composerArtifactMessage.id, actionKey)
    : undefined;
  const hydrateCurrentArtifact = useCallback((artifactId, isCancelled) => hydrateLatestArtifactTurn({
    artifactId, messages, isCancelled, updateAssistantMessage,
  }), [messages, updateAssistantMessage]);
  const chatBusy = processing || restoring || Boolean(exportingId);
  const displayedMessages = useGeneratedNextSteps({ sessionId, messages, busy: chatBusy, onCurrentArtifact: hydrateCurrentArtifact });

  const layoutSidebarProps = useMemo(() => ({
    ...sidebarProps,
    onSelectSession: async (targetSessionId) => {
      setSearchParams({});
      return sidebarProps.onSelectSession?.(targetSessionId);
    },
  }), [setSearchParams, sidebarProps]);

  return <ChatPageView {...{
      navigationData, layoutSidebarProps, handleStartNewChatFromShell, handleOpenLibrary,
      showLibrary, chatBusy, sessionId, handleOpenSessionFromLibrary,
      restoreError, messages: displayedMessages, connectionRetry: handleRetrySend || restoreRetry, processingId, exportingId,
      handleExportCompanies, artifactActionInFlight, handleRunArtifactAction, handleSendSuggestion,
      processing, restoring, handleSend, composerActions,
      composerActionInFlightKey, handleRunComposerAction, composerPrefill, exportReview,
      exportDialogError, setExportReview, setExportDialogError, confirmExportCompanies, actionConfirmation
    }} />;
}
